
<template>
    <table class="vue-tables table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
        <thead>
            <tr>
                <th>{{$t("logName")}}</th>
                <th>{{$t("marketplace")}}</th>
                <th>{{$t("errors")}}</th>
                <th>{{$t("operations")}}</th>
                <th>{{$t("status")}}</th>
                <th>{{$t("actions")}}</th>
            </tr>
        </thead>
        <tbody>
            <template v-if="logs.length">
                <tr v-for="(log, index) in logs" :key="log.id">
                    <td>{{ translateLogName(log.logName) }}
                        <br>
                        <span class="d-block ltr text-start" v-html="formatDate(log.created_at)"></span>
                    </td>
                    <td>
                        <img v-if="findMarketplaceById(log?.options?.marketplace_id)?.marketplace?.logo_url" class="w-30px"
                            :src="findMarketplaceById(log?.options?.marketplace_id)?.marketplace?.logo_url"
                            :alt="translateLogName(log.logName)">
                        <span v-else>--</span>
                    </td>
                    <td>{{ log?.errors_count }}</td>
                    <td>{{ log?.rows_count || 0 }}</td>
                    <td>
                        <span :class="statusBadgeClass(log.status)">
                            {{ translateStatus(log.status) }}
                        </span>
                    </td>
                    <td>
                        <a :href="`${locale}/logs/${log.id}`" target="_blank" class="edit-btn">
                            <i class="bi bi-eye fs-5"></i>
                        </a>
                    </td>
                </tr>
            </template>
            <template v-else>
                <tr class="text-center">
                    <td colspan="5">
                        {{$t('No Data')}}
                    </td>
                </tr>
            </template>
        </tbody>
    </table>
    <div class="pagination-controls">
		<button @click="prevPage" :disabled="currentPage <= 1">{{ $t('previous') }}</button>
		<template v-if="pagesOptions.length > 0 && pagesOptions[0] !== 1 && totalPages > 0">
			<button @click="goToPage(1)">
				{{ 1 }}
			</button>
			<span class="px-1 pt-2">...</span>
		</template>
		<span v-for="n in pagesOptions" :key="n">
			<button
				@click="goToPage(n)"
				:disabled="currentPage === n"
				:class="{ active: currentPage === n }"
			>
				{{ n }}
			</button>
		</span>
		<template
			v-if="pagesOptions.length > 0 && pagesOptions[pagesOptions.length - 1] !== totalPages && totalPages > 0"
		>
			<span class="px-1 pt-2">...</span>
            <button @click="goToPage(totalPages)">
				{{ totalPages }}
			</button>
		</template>
		<button @click="nextPage" :disabled="currentPage >= totalPages">{{ $t('next') }}</button>
	</div>
</template>
<script>
import { collection, getCountFromServer, query, orderBy, startAfter, limit, getDocs, where } from "firebase/firestore";
import { statusBadgeClass, formatDate } from '@/components/utils.js'
import { db } from "@/firebase";
import axios from "axios";

export default {
    props: { locale: null, marketplaces: {}, tenant: null, filters:null},
    data() {
        return {
            showLoading: false,
            products: null,
            marketplaces: [],
            logs: [],
            lastVisible: null, // To keep track of the last document in the current batch
            firstVisible: null, // To keep track of the first document in the current batch for previous page action
            currentPage: 1, // Assuming you have a way to know this
            totalLogs: 1, // Assuming you have a way to know this
            selectedMarketplaceId: '',
            translations: {
                LogActionsEnum: {},
                JobLogStatus: {}
            },
        };
    },
    watch: {
        filters: {
            deep: true,
            handler(newFilters) {
                this.loadLogs(1);
            }
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalLogs / 10);
        },
        pagesOptions() {
			if (this.log?.errors?.length == 0) return [];
			const PAGES_RANGE = 1;
			const result = [];
			const selectedPage = this.currentPage;

			for (let i = Math.max(1, selectedPage - PAGES_RANGE); i < selectedPage; i++) {
				result.push(i);
			}
			result.push(selectedPage);
			for (
				let i = selectedPage + 1;
				i <= Math.min(this.totalPages, selectedPage + PAGES_RANGE);
				i++
			) {
				result.push(i);
			}

			return result;
		},
    },
    async mounted() {
        this.$i18n.locale = this.locale;
        // TODO: remove
		  // await this.$store.dispatch('Chat/authenticateWithFirebase');
        this.loadLogs();
        this.fetchTranslations();
        this.fetchMarketplaces();

    },
    methods: {
        statusBadgeClass,
        formatDate,
        filterLogs() {
            this.loadLogs(1); // Reset to the first page when filtering
        },
        async fetchTranslations() {
            // Example API call to fetch translations
            const response = await axios.get('/core/list/enum-list?list[]=LogActionsEnum&list[]=JobLogStatus', {
                headers: {
                    'locale': this.locale
                }
            });
            if (response.data && response.data.result && response.data.result.lists) {
                this.translations = response.data.result.lists;
            }
        },
        async fetchMarketplaces() {
            try {
                const response = await axios.get(`${window.location.origin}/api/v1/linked-marketplaces`);
                this.marketplaces = response.data?.result?.linked_marketplaces?.data;
            } catch (error) {
                console.error("Error fetching marketplaces:", error);
            }
        },
        findMarketplaceById(marketplaceId) {
            return this.marketplaces?.find(marketplace => marketplace?.marketplace_id === marketplaceId) || null;
        },
        translateLogName(logName)  {
            return this.translations.LogActionsEnum[logName] || logName;
        },
        translateStatus(status)  {
            return this.translations.JobLogStatus[status] || status;
        },
        async loadLogs(page = 1) {
            this.showLoading = true;
            this.currentPage = page;
            try {
                const baseQuery = collection(db, `tenants/${this.tenant}/logs`);
                // Start with a basic query
                let conditions = [];

                // Add filter for marketplace if it exists
                if (this.filters.marketplace) {
                    conditions.push(where('options.marketplace_id', '==', +this.filters.marketplace));
                }
                let logsQuery = query(baseQuery, orderBy('created_at', 'desc'), ...conditions, limit(10));
                const snapshot = await getCountFromServer(baseQuery);
                this.totalLogs = snapshot.data().count;
                let updatedQuery = logsQuery;

                if (page > 1 && this.lastVisible) {
                    updatedQuery = query(logsQuery, startAfter(this.lastVisible));
                }

                const documentSnapshots = await getDocs(updatedQuery);

                // Get the last visible document
                this.lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
                this.firstVisible = documentSnapshots.docs[0];

                // Update logs
                this.logs = documentSnapshots.docs.map(doc => ({
                    id: doc.id, // Include the document ID
                    ...doc.data()
                }));
            } catch (error) {
                console.error('Failed to fetch logs:', error);
            } finally {
                this.showLoading = false;
            }
        },
        nextPage() {
			if (this.currentPage < this.totalPages) {
				this.currentPage++;
                this.loadLogs(this.currentPage);
			}
		},
		prevPage() {
			if (this.currentPage > 1) {
				this.currentPage--;
                this.loadLogs(this.currentPage);
			}
		},
		goToPage(pageNumber) {
			if (pageNumber >= 1 && pageNumber <= this.totalPages) {
				this.currentPage = pageNumber;
                this.loadLogs(this.currentPage);
			}
		},
    },
};
</script>
<style scoped lang="scss">
@import "../styles/index.scss";
</style>
